/**
 * What a crew looks like.
 *
 * The mark, the banner and the level bar, in one file, because all three are
 * drawn from the same appearance object and all three appear in four places:
 * the crew screen, the panel, the settings preview and the directory. Written
 * once so a crew looks like itself everywhere rather than nearly like itself.
 *
 * Every piece takes the crew's own colour and falls back to the app accent, so
 * a crew that has never opened its settings still looks deliberate.
 */

import type { ReactNode } from 'react';
import { fileUrl } from '@/lib/api';
import { cn } from '@/lib/cn';

export type CrewAppearance = {
  /** The crew's accent, or null for the app's. */
  color: string | null;
  banner: string | null;
  bannerColor: string | null;
  /** How the banner is framed, so everybody sees the same crop. */
  bannerCrop?: { x: number; y: number; zoom: number; fit: 'fit' | 'fill' };
  icon: string | null;
  background: string;
  /** How the tag chip is drawn beside a name, and in what letterform. */
  tagStyle: 'plate' | 'outline' | 'solid' | 'bracket' | 'bare';
  tagFont: 'mono' | 'sans' | 'wide';
  /* The earned half: see crewtags.js and CrewTag.tsx. Optional because a
     server that predates them sends an appearance without them. */
  tagText?: string | null;
  tagDesign?: string;
  /** Which of the small legible set the letters are drawn in. */
  tagInk?: string;
  tagMotion?: boolean;
};

export type CrewLevel = { level: number; into: number; need: number; total: number };

/** The colour everything else is mixed from. */
export function crewColor(a?: CrewAppearance | null, fallback = 'var(--accent)'): string {
  return a?.color || fallback;
}

/**
 * The named backdrops.
 *
 * CSS rather than images: they have to work at 44px on a chip and at 1200px
 * behind a header, and they have to take the crew's colour. Each is a pair of
 * layered gradients - cheap to paint and impossible to make unreadable,
 * because the text over them always sits on its own scrim.
 */
export function crewBackground(background: string, colour: string): string {
  const mix = (pct: number) => 'color-mix(in srgb, ' + colour + ' ' + pct + '%, transparent)';

  switch (background) {
    case 'mesh':
      return [
        'radial-gradient(60% 80% at 12% 18%, ' + mix(46) + ', transparent 62%)',
        'radial-gradient(52% 70% at 84% 30%, ' + mix(30) + ', transparent 60%)',
        'radial-gradient(70% 90% at 50% 108%, ' + mix(22) + ', transparent 62%)'
      ].join(',');

    case 'rays':
      return [
        'repeating-conic-gradient(from 210deg at 18% 116%, ' + mix(20) + ' 0deg 5deg, transparent 5deg 13deg)',
        'radial-gradient(80% 100% at 18% 112%, ' + mix(40) + ', transparent 70%)'
      ].join(',');

    case 'grid':
      return [
        'repeating-linear-gradient(0deg, ' + mix(13) + ' 0 1px, transparent 1px 22px)',
        'repeating-linear-gradient(90deg, ' + mix(13) + ' 0 1px, transparent 1px 22px)',
        'radial-gradient(90% 120% at 50% 0%, ' + mix(34) + ', transparent 68%)'
      ].join(',');

    case 'aurora':
      return [
        'radial-gradient(45% 120% at 22% 0%, ' + mix(44) + ', transparent 64%)',
        'radial-gradient(38% 110% at 52% 8%, color-mix(in srgb, var(--tier-mythic) 26%, transparent), transparent 62%)',
        'radial-gradient(48% 130% at 82% 0%, ' + mix(30) + ', transparent 66%)'
      ].join(',');

    case 'circuit':
      return [
        'repeating-linear-gradient(45deg, ' + mix(15) + ' 0 2px, transparent 2px 16px)',
        'repeating-linear-gradient(-45deg, ' + mix(10) + ' 0 2px, transparent 2px 24px)',
        'radial-gradient(80% 100% at 100% 0%, ' + mix(34) + ', transparent 66%)'
      ].join(',');

    case 'waves':
      return [
        'repeating-radial-gradient(120% 60% at 50% 130%, ' + mix(16) + ' 0 3px, transparent 3px 26px)',
        'radial-gradient(90% 90% at 50% 120%, ' + mix(36) + ', transparent 68%)'
      ].join(',');

    default:
      return 'linear-gradient(118deg, ' + mix(30) + ', transparent 66%)';
  }
}

/** Every design, with a readable name, for the settings picker. */
export const CREW_BACKGROUNDS: [string, string][] = [
  ['none', 'Plain'],
  ['mesh', 'Mesh'],
  ['rays', 'Rays'],
  ['grid', 'Grid'],
  ['aurora', 'Aurora'],
  ['circuit', 'Circuit'],
  ['waves', 'Waves']
];

/**
 * The crew's mark.
 *
 * Its picture if it has uploaded one, and its tag set in the crew colour if
 * not - never a blank square. The tag version is the default on purpose: a
 * tag is the thing a crew already has and the thing people recognise it by.
 */
export function CrewMark({ tag, appearance, size = 56, className }: {
  tag: string;
  appearance?: CrewAppearance | null;
  size?: number;
  className?: string;
}) {
  const colour = crewColor(appearance);
  const icon = appearance?.icon ? fileUrl(appearance.icon) : null;

  return (
    <span
      /* No border: a 1px border clipped round a picture is what drew the
         jagged rim. The edge is a soft inset line painted over the picture
         instead, and the clip itself is forced smooth. */
      className={cn('smooth-clip relative grid flex-none place-items-center overflow-hidden rounded-[var(--radius)]', className)}
      style={{
        width: size,
        height: size,
        background: icon ? 'var(--bg-2)' : 'color-mix(in srgb, ' + colour + ' 14%, var(--bg-2))',
        color: colour,
        // The tag has to shrink with the plate, or a five-character tag in a
        // 36px mark runs straight out of it.
        fontSize: Math.max(9, Math.round(size * 0.26))
      }}
    >
      {icon
        ? <img src={icon} alt="" className="h-full w-full object-cover" />
        : <span className="font-mono font-extrabold tracking-[.04em]">{tag}</span>}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, ' + colour + ' 34%, var(--line))' }}
      />
    </span>
  );
}

/**
 * The strip behind a crew's name.
 *
 * An uploaded banner wins; otherwise the chosen design in the crew's own
 * colour. Either way the bottom is darkened, so whatever sits over it stays
 * readable without the banner having to be dim.
 */
export function CrewBanner({ appearance, className, children }: {
  appearance?: CrewAppearance | null;
  className?: string;
  children?: ReactNode;
}) {
  const colour = crewColor(appearance);
  const banner = appearance?.banner ? fileUrl(appearance.banner) : null;
  const base = appearance?.bannerColor || colour;

  return (
    /* pugo-panel: a banner is a surface like any other, and a scene that
       frames every panel and not this one leaves the one picture on the
       screen looking like it belongs to a different app. */
    <div className={cn('relative overflow-hidden', className)}>
      <span
        aria-hidden
        className="absolute inset-0"
        style={{
          background: banner
            ? 'var(--bg-2)'
            : crewBackground(appearance?.background ?? 'none', base) + ', var(--bg-2)'
        }}
      />
      {banner ? <img src={banner} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}

      {/* Solid at the very bottom and gone by halfway, so a bright banner
          cannot swallow the name while the top of it stays untouched. */}
      <span
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-[72%]"
        style={{
          background:
            'linear-gradient(to top, var(--bg-1) 0%, var(--bg-1) 34%,' +
            ' color-mix(in srgb, var(--bg-1) 78%, transparent) 56%, transparent 100%)'
        }}
      />
      {children ? <div className="relative">{children}</div> : null}
    </div>
  );
}

/**
 * How far the crew is through its level.
 *
 * The number that fills it is the total level of everyone in the crew, which
 * is also what ranks it - so the bar and the leaderboard can never tell two
 * different stories about how a crew is doing.
 */
export function CrewLevelBar({ level, appearance, showNumbers = true }: {
  level: CrewLevel;
  appearance?: CrewAppearance | null;
  showNumbers?: boolean;
}) {
  const colour = crewColor(appearance);
  const pct = level.need > 0 ? Math.min(100, Math.round((level.into / level.need) * 100)) : 100;

  return (
    <div>
      <div className="mb-1.5 flex items-baseline gap-2">
        <span className="text-[13px] font-extrabold" style={{ color: colour }}>
          Level {level.level}
        </span>
        {showNumbers ? (
          <span className="ml-auto text-[11.5px] text-mute tabular-nums">
            {level.into} / {level.need}
            <span className="text-ghost"> combined member levels to level {level.level + 1}</span>
          </span>
        ) : null}
      </div>

      <div className="h-[7px] overflow-hidden rounded-full bg-surface-3">
        <span
          className="block h-full rounded-full transition-[width] duration-500"
          style={{
            width: pct + '%',
            background: 'linear-gradient(90deg, color-mix(in srgb, ' + colour + ' 62%, transparent), ' + colour + ')',
            boxShadow: '0 0 10px color-mix(in srgb, ' + colour + ' 55%, transparent)'
          }}
        />
      </div>
    </div>
  );
}
